import { Link, useLocation, useNavigate } from "react-router-dom";
import { Menu, X, Globe, ChevronDown } from "lucide-react";
import { useEffect, useState } from "react";
import { toast } from "sonner";
import { useTranslation } from "react-i18next";
import { useAuth } from "../lib/auth";
import { api } from "../lib/api";
import { setLanguage, currentLanguage } from "../i18n";
import { useSpanishAvailable } from "../lib/esGate";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuLabel, DropdownMenuSeparator, DropdownMenuTrigger } from "./ui/dropdown-menu";
import { track } from "../lib/analytics";

export function LanguageSwitcher({ dark = false }) {
  const { user, refresh } = useAuth();
  const lang = currentLanguage();
  const esAvailable = useSpanishAvailable();
  const switchTo = async (l) => {
    setLanguage(l);
    if (user) {
      try { await api.post("/account/language", { language: l }); refresh(); } catch (e) { console.debug("Language preference save failed (non-blocking):", e); }
    }
  };
  if (!esAvailable) return null;
  return (
    <div className={`flex items-center gap-1 text-xs font-semibold border rounded-full px-1 py-0.5 ${dark ? "border-slate-700 text-slate-300" : "border-slate-200 text-slate-500"}`} data-testid="language-switcher">
      <Globe size={13} className="ml-1" />
      {["en", "es"].map((l) => (
        <button key={l} onClick={() => switchTo(l)} aria-label={`Switch to ${l}`}
          className={`px-2 py-0.5 rounded-full uppercase transition-colors max-sm:min-w-[44px] max-sm:min-h-[44px] max-sm:inline-flex max-sm:items-center max-sm:justify-center ${lang === l ? "bg-emerald-700 text-white" : "hover:text-emerald-700"}`}
          data-testid={`lang-${l}`}>{l}</button>
      ))}
    </div>
  );
}

export const DISCLAIMER_TEXT = "disclaimer"; // rendered via i18n: t("disclaimer")

export function Header() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const { pathname } = useLocation();
  // Any navigation (logo, back button, in-page link) closes the mobile menu, not just the menu's own links.
  useEffect(() => { setOpen(false); }, [pathname]);
  const learnLinks = [
    { to: "/guide", label: t("nav.guide") },
    { to: "/tools", label: t("nav.freeTools") },
    { to: "/blog", label: t("nav.blog") },
    { to: "/state-regulators", label: t("nav.regulators") },
  ];
  return (
    <header className="sticky top-0 z-50 backdrop-blur-md bg-white/85 border-b border-slate-200/80" data-testid="site-header">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-16 flex items-center justify-between">
        <Link to="/" className="flex items-center gap-2" data-testid="header-logo">
          <img src="/brand/logo-horizontal-light.webp" alt="Denial Decoder" width="393" height="88" className="h-11 w-auto" />
        </Link>
        <nav aria-label="Main" className="hidden md:flex items-center gap-6 text-sm font-medium text-slate-600">
          <Link to="/how-it-works" className="hover:text-emerald-700 transition-colors" data-testid="nav-how-it-works">{t("nav.howItWorks")}</Link>
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button className="hover:text-emerald-700 transition-colors inline-flex items-center gap-1" data-testid="nav-learn">
                {t("nav.learn")} <ChevronDown size={14} />
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="start" data-testid="nav-learn-menu">
              {learnLinks.map((l) => (
                <DropdownMenuItem key={l.to} onSelect={() => navigate(l.to)} data-testid={`nav-learn-${l.to.slice(1)}`}>{l.label}</DropdownMenuItem>
              ))}
            </DropdownMenuContent>
          </DropdownMenu>
          <Link to="/pricing" className="hover:text-emerald-700 transition-colors" data-testid="nav-pricing">{t("nav.pricing")}</Link>
          <Link to="/about" className="hover:text-emerald-700 transition-colors" data-testid="nav-about">{t("nav.about")}</Link>
          {user?.is_admin && <Link to="/admin" className="hover:text-emerald-700" data-testid="nav-admin">{t("nav.admin")}</Link>}
          {user ? (
            <>
              <Link to="/app/cases" className="hover:text-emerald-700" data-testid="nav-my-cases">My appeals</Link>
              <DropdownMenu onOpenChange={(o) => o && track("account_menu_opened")}>
                <DropdownMenuTrigger asChild>
                  <button className="w-9 h-9 rounded-full bg-emerald-600 text-white text-sm font-bold flex items-center justify-center hover:bg-emerald-700 transition-colors"
                    title={user.email} aria-label="Account menu" data-testid="account-menu-trigger">
                    {(user.name || user.email || "?").trim().split(/\s+/).map((w) => w[0]).slice(0, 2).join("").toUpperCase()}
                  </button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-56" data-testid="account-menu">
                  <DropdownMenuLabel className="text-xs font-normal text-slate-500 truncate" data-testid="account-menu-email">{user.email}</DropdownMenuLabel>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem onSelect={() => navigate("/app/cases")} data-testid="account-menu-cases">My appeals</DropdownMenuItem>
                  <DropdownMenuItem onSelect={() => navigate("/app/documents")} data-testid="account-menu-documents">Documents</DropdownMenuItem>
                  <DropdownMenuItem onSelect={() => navigate("/app/settings/billing")} data-testid="account-menu-billing">Billing & plan</DropdownMenuItem>
                  <DropdownMenuItem onSelect={() => navigate("/app/settings")} data-testid="account-menu-settings">Settings</DropdownMenuItem>
                  <DropdownMenuItem onSelect={() => navigate("/app/help")} data-testid="account-menu-help">Help</DropdownMenuItem>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem onSelect={() => { logout(); navigate("/"); }} data-testid="account-menu-logout">{t("nav.logout")}</DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            </>
          ) : (
            <Link to="/login" className="hover:text-emerald-700" data-testid="nav-login">{t("nav.login")}</Link>
          )}
          <LanguageSwitcher />
          <Button onClick={() => { track("cta_click", { location: "sticky" }); navigate("/check"); }} className="bg-emerald-700 hover:bg-emerald-800 rounded-full px-6 font-bold shadow-md shadow-emerald-600/25" data-testid="check-denial-free-header-cta">
            {t("nav.cta")}
          </Button>
        </nav>
        <button className="md:hidden min-w-[44px] min-h-[44px] inline-flex items-center justify-center -mr-2" onClick={() => setOpen(!open)} data-testid="mobile-menu-toggle"
          aria-label={open ? "Close menu" : "Open menu"} aria-expanded={open} aria-controls="mobile-menu">
          {open ? <X size={22} /> : <Menu size={22} />}
        </button>
      </div>
      {open && (
        <nav id="mobile-menu" aria-label="Main" className="md:hidden border-t border-slate-200 bg-white px-4 py-4 flex flex-col gap-3 text-sm font-medium max-h-[calc(100dvh-4rem)] overflow-y-auto" data-testid="mobile-menu">
          <Link to="/how-it-works" onClick={() => setOpen(false)} data-testid="mobile-nav-how-it-works">{t("nav.howItWorks")}</Link>
          <div className="border-t border-slate-100 pt-3" data-testid="mobile-learn-group">
            <p className="text-xs font-bold uppercase tracking-wider text-slate-500 mb-2">{t("nav.learn")}</p>
            <div className="flex flex-col gap-3 pl-3 border-l-2 border-emerald-100">
              {learnLinks.map((l) => <Link key={l.to} to={l.to} onClick={() => setOpen(false)} data-testid={`mobile-nav-${l.to.slice(1)}`}>{l.label}</Link>)}
            </div>
          </div>
          <Link to="/pricing" onClick={() => setOpen(false)} data-testid="mobile-nav-pricing">{t("nav.pricing")}</Link>
          <Link to="/about" onClick={() => setOpen(false)} data-testid="mobile-nav-about">{t("nav.about")}</Link>
          {user?.is_admin && <Link to="/admin" onClick={() => setOpen(false)}>{t("nav.admin")}</Link>}
          {user ? (
            <div className="border-t border-slate-100 pt-3 mt-1 flex flex-col gap-3" data-testid="mobile-account-section">
              <p className="text-xs font-bold uppercase tracking-wider text-slate-500">Account · <span className="normal-case font-medium">{user.email}</span></p>
              <Link to="/app/cases" onClick={() => setOpen(false)} data-testid="mobile-my-cases">My appeals</Link>
              <Link to="/app/documents" onClick={() => setOpen(false)}>Documents</Link>
              <Link to="/app/settings/billing" onClick={() => setOpen(false)}>Billing & plan</Link>
              <Link to="/app/settings" onClick={() => setOpen(false)}>Settings</Link>
              <Link to="/app/help" onClick={() => setOpen(false)}>Help</Link>
              <button onClick={() => { logout(); setOpen(false); navigate("/"); }} className="text-left" data-testid="mobile-logout">{t("nav.logout")}</button>
            </div>
          ) : <Link to="/login" onClick={() => setOpen(false)}>{t("nav.login")}</Link>}
          <LanguageSwitcher />
          <Button onClick={() => { setOpen(false); track("cta_click", { location: "sticky" }); navigate("/check"); }} className="bg-emerald-700 hover:bg-emerald-800 rounded-full" data-testid="check-denial-free-mobile-cta">{t("nav.cta")}</Button>
        </nav>
      )}
    </header>
  );
}

export function Footer() {
  const { t, i18n } = useTranslation();
  const [hasResults, setHasResults] = useState(false);
  const [hubs, setHubs] = useState([]);
  useEffect(() => { api.get("/testimonials").then((r) => setHasResults((r.data || []).length > 0)).catch(() => {}); }, []);
  useEffect(() => { api.get("/claim-types").then((r) => setHubs((r.data || []).filter((x) => x.status === "live" && x.has_hub))).catch(() => {}); }, []);
  return (
    <footer className="bg-slate-900 text-slate-300 mt-24" data-testid="site-footer">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-8 text-sm">
          <div>
            <div className="mb-3">
              <img src="/brand/logo-horizontal-dark.webp" alt="Denial Decoder" width="393" height="88" className="h-9 w-auto" data-testid="footer-logo" />
            </div>
            <p className="text-slate-400">{t("footer.tagline")}</p>
          </div>
          <div>
            <p className="text-white font-semibold mb-3">{t("footer.product")}</p>
            <div className="flex flex-col gap-2">
              <Link to="/check" className="hover:text-emerald-400">{t("footer.checkFree")}</Link>
              <Link to="/pricing" className="hover:text-emerald-400">{t("footer.pricing")}</Link>
              <Link to="/how-it-works" className="hover:text-emerald-400">{t("footer.howItWorks")}</Link>
            </div>
          </div>
          <div>
            <p className="text-white font-semibold mb-3">{t("footer.resources")}</p>
            <div className="flex flex-col gap-2">
              <Link to="/blog" className="hover:text-emerald-400">{t("footer.blog")}</Link>
              <Link to="/state-regulators" className="hover:text-emerald-400">{t("footer.regulators")}</Link>
              {hasResults && <Link to="/results" className="hover:text-emerald-400" data-testid="footer-results-link">{t("footer.results")}</Link>}
              {hubs.map((h) => (
                <Link key={h.slug} to={`/${h.hub_slug}`} className="hover:text-emerald-400" data-testid={`footer-hub-${h.slug}`}>
                  {((i18n.language || "").startsWith("es") && h.es?.plural) || h.plural || h.name}
                </Link>
              ))}
              {hubs.length === 0 && <Link to="/home-warranty-denials" className="hover:text-emerald-400">{t("footer.hwDenials")}</Link>}
            </div>
          </div>
          <div>
            <p className="text-white font-semibold mb-3">{t("footer.legal")}</p>
            <div className="flex flex-col gap-2">
              <Link to="/about" className="hover:text-emerald-400" data-testid="footer-about">{t("footer.about")}</Link>
              <Link to="/terms" className="hover:text-emerald-400">{t("footer.terms")}</Link>
              <Link to="/privacy" className="hover:text-emerald-400">{t("footer.privacy")}</Link>
              <Link to="/refund" className="hover:text-emerald-400">{t("footer.refund")}</Link>
              <a href="mailto:support@decodemydenial.com" className="hover:text-emerald-400" data-testid="footer-contact">{t("footer.contact")}</a>
              <button onClick={() => window.dispatchEvent(new Event("dd:cookie-settings"))} className="text-left hover:text-emerald-400" data-testid="cookie-settings-link">{t("footer.cookieSettings")}</button>
            </div>
          </div>
        </div>
        <NewsletterSignup />
        <div className="border-t border-slate-800 mt-10 pt-6 text-xs text-slate-400 leading-relaxed" data-testid="footer-disclaimer">
          {t("disclaimer")}
          <p className="mt-2" data-testid="footer-trademark-disclaimer">All company and product names referenced on this site are trademarks of their respective owners. Denial Decoder is not affiliated with, endorsed by, sponsored by, or authorized by any of the companies named on this site.</p>
        </div>
        <p className="text-xs text-slate-600 mt-4">© {new Date().getFullYear()} Denial Decoder — decodemydenial.com</p>
      </div>
    </footer>
  );
}

function NewsletterSignup() {
  const { t } = useTranslation();
  const [email, setEmail] = useState("");
  const [done, setDone] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    try {
      await api.post("/newsletter/subscribe", { email, source: "footer", language: currentLanguage() });
      setDone(true);
    } catch {
      toast.error("Could not subscribe — try again");
    }
  };
  return (
    <div className="mt-10 bg-slate-800/60 rounded-2xl p-6" data-testid="footer-newsletter">
      <p className="text-white font-semibold">{t("footer.newsletterTitle")}</p>
      <p className="text-sm text-slate-400 mt-1 mb-4">{t("footer.newsletterSub")}</p>
      {done ? (
        <p className="text-emerald-400 text-sm font-medium" data-testid="newsletter-footer-success">{t("footer.newsletterDone")}</p>
      ) : (
        <form onSubmit={submit} className="flex flex-col sm:flex-row gap-2.5 max-w-md w-full">
          <Input type="email" required value={email} onChange={(e) => setEmail(e.target.value)} placeholder="you@example.com"
            className="bg-slate-900 border-slate-700 text-white h-11 sm:h-9 w-full" data-testid="newsletter-footer-input" />
          <Button type="submit" className="bg-emerald-700 hover:bg-emerald-800 rounded-full shrink-0 h-11 sm:h-9 w-full sm:w-auto" data-testid="newsletter-footer-submit">{t("footer.subscribe")}</Button>
        </form>
      )}
    </div>
  );
}

export function InfoBanner() {
  const { t } = useTranslation();
  return (
    <div className="bg-amber-50 border border-amber-200 text-amber-900 text-sm rounded-lg px-4 py-3 mb-6" data-testid="info-banner">
      {t("infoBanner")}
    </div>
  );
}

export default function Layout({ children }) {
  return (
    <div className="min-h-screen flex flex-col">
      <a href="#main-content" className="sr-only focus:not-sr-only focus:fixed focus:top-3 focus:left-3 focus:z-[60] focus:bg-white focus:text-emerald-800 focus:font-semibold focus:px-4 focus:py-2 focus:rounded-full focus:shadow-lg focus:ring-2 focus:ring-emerald-600" data-testid="skip-to-content">
        Skip to main content
      </a>
      <Header />
      <main id="main-content" tabIndex={-1} className="flex-1 focus:outline-none">{children}</main>
      <Footer />
    </div>
  );
}
